import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { buildJobDetailViewModel } from "components/Logistics/Pickups/Details/buildJobDetailViewModel";
import { mockLogisticsTicketDetail } from "components/Logistics/Pickups/Details/mockLogisticsTicketDetail";
import { JobDetailViewModel } from "components/Logistics/Pickups/Details/jobDetailTypes";
import { api } from "services/api.service";
import { LogisticsTicket } from "types/logisticsTicket";

export default function useLogisticsTicket(ticketUuid?: string) {
  const { pickupUuid } = useParams();
  const uuid = ticketUuid ?? pickupUuid;
  const [job, setJob] = useState<JobDetailViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTicket = useCallback(() => {
    if (!uuid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    api
      .get<LogisticsTicket>(`/logistics_tickets/${uuid}`)
      .then(({ data }) => {
        setJob(buildJobDetailViewModel(data));
      })
      .catch((fetchError: Error) => {
        if (uuid === mockLogisticsTicketDetail.ticketUuid) {
          setJob(mockLogisticsTicketDetail);
          setError(null);
          return;
        }

        setError(fetchError);
        setJob(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [uuid]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const refetch = useCallback(() => fetchTicket(), [fetchTicket]);

  const applyTicket = useCallback((ticket: LogisticsTicket) => {
    const nextJob = buildJobDetailViewModel(ticket);
    if (nextJob) {
      setJob(nextJob);
    }
  }, []);

  return useMemo(
    () => ({
      job,
      loading,
      error,
      refetch,
      applyTicket,
    }),
    [job, loading, error, refetch, applyTicket],
  );
}
